import { useCallback, useEffect, useRef } from "react";
import { type DocTypeConfig, markSectionInserted } from "../../api/client";
import { splitDraftBySections } from "../../services/formatter/sectionSplitter";
import {
  insertSectionContent,
  populateBatchPlaceholder,
  scaffoldDocument,
  skeletonFromTuples,
  updateTitlePageFields,
} from "../../services/wordWriter";
import type { ProgressEvent } from "../../types";
import { runPostSectionCitationPipeline } from "../services/citationPipeline";
import { sanitizeDraftForWord } from "../services/draftSanitizer";

export interface UseWordWriteQueueResult {
  /** Passed as onProgressEvent to useVariantCRun. */
  handleProgressEvent: (event: ProgressEvent) => void;
  /** Idempotency guard — reset to false on each new run start / reset.
   *  Callers mutate `.current` directly. */
  scaffoldedRef: React.MutableRefObject<boolean>;
  /** Insertion mode for the active run. Callers mutate `.current`.
   *  "section" — insert each section live; "batch" — backend assembles final docx. */
  insertionModeRef: React.MutableRefObject<"section" | "batch">;
  /** Wall-clock stamp of the most recent agent write. Passed to useWordEdits
   *  as agentWriteRef to suppress false-positive "you edited" events. */
  agentLastWriteAtRef: React.MutableRefObject<number>;
  /** Chain an arbitrary Word-write closure onto the same single-writer
   *  queue used by SSE section_ready / draft_ready writes. Used by the
   *  bootstrap rehydrate (`loadRunIntoLiveDoc`) so the rehydrate runs to
   *  completion before any subsequent SSE write touches Word — never
   *  two Word.run consumers contending for the Office.js context. */
  enqueueWordWrite: <T>(fn: () => Promise<T>) => Promise<T>;
}

/**
 * Owns the Word-write side-effect path triggered by SSE progress events:
 *
 *   gate_start  → scaffoldDocument (once per run, idempotency via scaffoldedRef)
 *   section_ready → insertSectionContent (serialised through wordWriteQueueRef)
 *   draft_ready with payload_type:"title_fields" → updateTitlePageFields
 *
 * All Word.run-bound writes are serialised through a single promise chain
 * (wordWriteQueueRef) to prevent concurrent Office.js context corruption.
 *
 * @param docConfig — the active doc-type config; used by scaffoldDocument.
 */
export function useWordWriteQueue(docConfig: DocTypeConfig | null): UseWordWriteQueueResult {
  // Scaffolding idempotency guard. gate_start may fire multiple times
  // (per-gate); scaffold only once per run. Callers reset to false on
  // RESET_TO_IDLE / run start.
  const scaffoldedRef = useRef(false);

  // Insertion mode captured at run-start so a mid-run toggle can't
  // confuse the side-effect path.
  const insertionModeRef = useRef<"section" | "batch">("section");

  // Single-writer queue — see decision note in the shell comment block.
  const wordWriteQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Wall-clock stamp of the most recent agent write; consumed by
  // useWordEdits to suppress false-positive edit attribution.
  const agentLastWriteAtRef = useRef<number>(0);

  // Read docConfig through a ref so handleProgressEvent's identity
  // does not flip whenever the shell rebuilds the config object
  // (re-fetch on bootstrap, memo invalidation). A stable callback
  // identity keeps downstream consumers — useVariantCRun's SSE
  // subscription effect, in particular — from re-subscribing on
  // unrelated config-object churn.
  const docConfigRef = useRef(docConfig);
  useEffect(() => {
    docConfigRef.current = docConfig;
  }, [docConfig]);

  const handleProgressEvent = useCallback((event: ProgressEvent) => {
    if (insertionModeRef.current === "batch" && event.event_type === "gate_start" && !scaffoldedRef.current) {
      scaffoldedRef.current = true;
      const config = docConfigRef.current;
      const displayName = config?.display_name || "";
      const standard = config?.standard;
      const shouldAppendStandard = standard && !displayName.includes(standard);
      const docTypeLabel =
        [displayName, shouldAppendStandard ? `(${standard})` : null].filter(Boolean).join(" ") || "document";
      wordWriteQueueRef.current = wordWriteQueueRef.current.then(() =>
        populateBatchPlaceholder(docTypeLabel).catch((err) => {
          console.warn("[useWordWriteQueue] batch placeholder failed (non-fatal):", err);
          // Allow a future gate_start to retry.
          scaffoldedRef.current = false;
        }),
      );
      return;
    }

    if (insertionModeRef.current === "batch" && event.event_type === "section_ready") return;

    if (insertionModeRef.current === "section" && event.event_type === "gate_start" && !scaffoldedRef.current) {
      scaffoldedRef.current = true;
      const config = docConfigRef.current;
      if (!config) return;
      try {
        // title_page is loosely typed on the DocTypeConfig contract;
        // the YAML's fields/confidentiality shape matches what
        // scaffoldDocument expects. Mirror the classic UI's call shape
        // via the same intentional cast — config drift would surface
        // here as a runtime no-op (scaffoldDocument tolerates undefined).
        const titlePage = config.title_page as
          | {
              fields?: Array<{ label: string; fill: string }>;
              confidentiality?: string;
            }
          | undefined;
        scaffoldDocument({
          skeleton: skeletonFromTuples(config.skeleton),
          documentTitle: config.display_name?.toUpperCase() || "",
          titlePageFields: titlePage?.fields,
          confidentiality: titlePage?.confidentiality,
          // Official Word numbering for live-drafted docs — same regime
          // as the server-rendered .docx (falls back to literal numbers
          // when the skeleton isn't positionally representable). Suppressed
          // when the doc-type opts out of heading numbers.
          nativeNumbering: config.heading_numbering !== "none",
          suppressHeadingNumbers: config.heading_numbering === "none",
        }).catch((err) => {
          console.warn("[useWordWriteQueue] scaffold failed (non-fatal):", err);
          // Allow a future gate_start to retry.
          scaffoldedRef.current = false;
        });
      } catch (err) {
        console.warn("[useWordWriteQueue] scaffold setup failed:", err);
        scaffoldedRef.current = false;
      }
      return;
    }

    if (event.event_type === "section_ready") {
      const resp = event.section_response as
        | {
            sections?: Array<{
              section_number?: string;
              draft_content?: string;
            }>;
          }
        | undefined;
      const sections = resp?.sections ?? [];
      // Every section number this module declares (primary +
      // breadcrumb children). The split distributes the primary
      // draft's "## N.x" blocks into these numbered skeleton
      // headings so each sub-heading owns its content.
      const allNums = sections.map((s) => s.section_number).filter((n): n is string => !!n);
      for (const s of sections) {
        if (!s.section_number || !s.draft_content) continue;
        // Strip the agent's subtask-manifest fence + SUBTASK-BEGIN/END
        // flag lines before writing to Word.
        const cleaned = sanitizeDraftForWord(s.draft_content);
        if (!cleaned) continue;
        // Capture for the queued closure — avoids stale reference
        // across the async queue boundary.
        const sectionNumber = s.section_number;
        // Distribute draft across the module's numbered skeleton
        // sub-sections. Single-section modules collapse to one chunk.
        const chunks = Object.entries(splitDraftBySections(cleaned, allNums, sectionNumber)).filter(
          ([, c]) => c.trim().length > 0,
        );
        // Chain onto the single-writer queue to prevent concurrent
        // Word.run blocks from corrupting paragraph indices.
        wordWriteQueueRef.current = wordWriteQueueRef.current.then(() =>
          (async () => {
            for (const [secNum, chunk] of chunks) {
              await insertSectionContent({
                sectionNumber: secNum,
                content: chunk,
                scrollAfterInsert: true,
              });
              // Single post-section pipeline: wrap each ``[N]`` token in
              // a tagged inline content control + refresh the marker-
              // event watcher. Hoisted out of this hook so the post-run
              // hydration path (``loadRunIntoLiveDoc``) can share the
              // exact same call — keeps the two write paths from
              // drifting on how citation markers become clickable.
              // ``runPostSectionCitationPipeline`` swallows its own
              // errors; we still await so the next
              // ``insertSectionContent`` runs against settled state.
              await runPostSectionCitationPipeline(secNum);
            }
          })()
            .then(() => {
              agentLastWriteAtRef.current = Date.now();
              // Batch-SSOT: tell the server this section is now in
              // the live doc so its lifecycle advances to ``inserted``.
              // Side-effect — must not break the write path. The server
              // self-walks ``drafted`` -> (``verified``) -> ``inserted``
              // (section_state.transition, 2026-08-01), so this single call
              // is legal from a freshly-drafted section and is idempotent on
              // repeat; any residual failure is non-fatal and swallowed.
              const runId = event.run_id;
              const moduleId = event.module_id;
              if (runId && moduleId) {
                void markSectionInserted(runId, moduleId).catch(() => {
                  /* non-fatal */
                });
              }
            })
            .catch((err: unknown) => {
              // Stamp anyway so useWordEdits doesn't attribute the
              // partial write to the writer.
              agentLastWriteAtRef.current = Date.now();
              console.warn("[useWordWriteQueue] insertSectionContent failed for §%s (non-fatal):", sectionNumber, err);
              // Surface OfficeExtension.Error.debugInfo — the plain
              // err.message is intentionally generic in the Office.js
              // SDK; debugInfo carries the structured detail.
              const oe = err as {
                code?: string;
                debugInfo?: unknown;
                message?: string;
                name?: string;
                stack?: string;
              };
              if (oe.debugInfo) {
                console.warn(
                  "[useWordWriteQueue] OfficeExtension.Error.debugInfo for §%s:",
                  sectionNumber,
                  oe.debugInfo,
                );
              }
            }),
        );
      }
    }

    // Title-page modules deliver output via draft_ready with
    // payload_type:"title_fields". Serialised with section writes
    // to prevent racing with a section insertion's deletion loop.
    // Section mode ONLY: the scaffolded title page exists in the live
    // doc and gets its fields filled in place. Batch mode holds just
    // the placeholder text — writing here created a stray title table
    // under it. Batch title fields reach the assembled docx via the
    // backend renderer (word_writer._add_title_page) and reach the
    // live doc on Load via loadRunIntoLiveDoc.
    if (event.event_type === "draft_ready") {
      if (insertionModeRef.current === "batch") return;
      const resp = event.section_response as { payload_type?: string; fields?: Record<string, string> } | undefined;
      if (resp?.payload_type === "title_fields" && resp.fields) {
        const fields = resp.fields;
        // title_page.style + statement_fields ride the same loosely
        // typed doc-type config blob as the scaffold call above.
        const tpConfig = docConfigRef.current?.title_page as
          | {
              style?: import("../../services/wordWriter").TitlePageStyle;
              statement_fields?: string[];
            }
          | undefined;
        wordWriteQueueRef.current = wordWriteQueueRef.current.then(() =>
          updateTitlePageFields(fields, tpConfig?.style, tpConfig?.statement_fields)
            .then(() => {
              agentLastWriteAtRef.current = Date.now();
            })
            .catch((err) => {
              agentLastWriteAtRef.current = Date.now();
              console.warn("[useWordWriteQueue] updateTitlePageFields failed (non-fatal):", err);
            }),
        );
      }
    }
  }, []);

  // Chain an arbitrary write onto the single-writer queue. Rejections
  // are absorbed on the queue tail so a failing rehydrate can't stall
  // subsequent SSE writes; the returned promise still reflects the
  // real outcome so callers can show their own error UI.
  const enqueueWordWrite = useCallback(<T>(fn: () => Promise<T>): Promise<T> => {
    const next = wordWriteQueueRef.current.then(() => fn());
    wordWriteQueueRef.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }, []);

  return {
    handleProgressEvent,
    scaffoldedRef,
    insertionModeRef,
    agentLastWriteAtRef,
    enqueueWordWrite,
  };
}
